import api from './base.api';
import type { Note } from '@/context/NotesContext';

const BASE_PATH = '/mcp/tools';

// API response interfaces that match the dynamic tools API
interface DynamicToolResponse {
  id: string;
  project_id: string;
  tool_name: string;
  tool_description: string;
  tool_body: string;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

interface DynamicToolListResponse {
  tools: DynamicToolResponse[];
  total: number;
}

interface DynamicToolCreate {
  project_id: string;
  tool_name: string;
  tool_description: string;
  tool_body: string;
  is_active: boolean;
}

interface DynamicToolUpdate {
  tool_name?: string;
  tool_description?: string;
  tool_body?: string;
  is_active?: boolean;
}

/**
 * Transform API response to Note interface
 */
const transformToNote = (apiResponse: DynamicToolResponse): Note => ({
  id: apiResponse.id,
  toolName: apiResponse.tool_name,
  toolDescription: apiResponse.tool_description,
  toolBody: apiResponse.tool_body,
  isActive: apiResponse.is_active,
  createdAt: apiResponse.created_at,
  updatedAt: apiResponse.updated_at || apiResponse.created_at,
});

/**
 * Transform Note to API request format
 */
const transformToApiCreate = (projectId: string, toolName: string, toolDescription: string, toolBody: string, isActive: boolean = true): DynamicToolCreate => ({
  project_id: projectId,
  tool_name: toolName,
  tool_description: toolDescription,
  tool_body: toolBody,
  is_active: isActive,
});

/**
 * Transform Note update to API request format
 */
const transformToApiUpdate = (toolName?: string, toolDescription?: string, toolBody?: string, isActive?: boolean): DynamicToolUpdate => ({
  ...(toolName !== undefined && { tool_name: toolName }),
  ...(toolDescription !== undefined && { tool_description: toolDescription }),
  ...(toolBody !== undefined && { tool_body: toolBody }),
  ...(isActive !== undefined && { is_active: isActive }),
});

export const notesApi = {
  /**
   * Fetch all notes for a project
   */
  async fetchNotes(projectId: string, search?: string): Promise<Note[]> {
    try {
      const params: any = {
        project_id: projectId,
        active_only: false,
      };

      // Add search parameters if search is provided
      if (search && search.trim()) {
        params.search = search.trim();
        params.search_fields = ['tool_name', 'tool_description', 'tool_body'];
      }

      const response = await api.get<DynamicToolListResponse>(`${BASE_PATH}/`, {
        params,
      });

      // Transform all tools to notes
      const notes = response.data.tools.map(transformToNote);

      return notes;
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      throw new Error('Failed to fetch notes');
    }
  },

  /**
   * Create a new note
   */
  async createNote(projectId: string, toolName: string, toolDescription: string, toolBody: string, isActive: boolean = true): Promise<Note> {
    try {
      const createData = transformToApiCreate(projectId, toolName, toolDescription, toolBody, isActive);
      const response = await api.post<DynamicToolResponse>(`${BASE_PATH}/`, createData);

      return transformToNote(response.data);
    } catch (error: any) {
      console.error('Failed to create note:', error);

      // Check if this is a duplicate name error from our enhanced error
      if (error?.response?.status === 400 && error?.response?.data?.detail?.includes('already exists')) {
        throw new Error(`Duplicate: A note with the name "${toolName}" already exists in this project`);
      }

      throw new Error('Failed to create note');
    }
  },

  /**
   * Update an existing note
   */
  async updateNote(noteId: string, toolName?: string, toolDescription?: string, toolBody?: string, isActive?: boolean): Promise<Note> {
    try {
      const updateData = transformToApiUpdate(toolName, toolDescription, toolBody, isActive);
      const response = await api.put<DynamicToolResponse>(`${BASE_PATH}/${noteId}`, updateData);

      return transformToNote(response.data);
    } catch (error: any) {
      console.error('Failed to update note:', error);

      // Check if this is a duplicate name error from our enhanced error
      if (error?.response?.status === 400 && error?.response?.data?.detail?.includes('already exists')) {
        throw new Error(`Duplicate: A note with the name "${toolName}" already exists in this project`);
      }

      throw new Error('Failed to update note');
    }
  },

  /**
   * Delete a note
   */
  async deleteNote(noteId: string): Promise<void> {
    try {
      await api.delete(`${BASE_PATH}/${noteId}`);
    } catch (error) {
      console.error('Failed to delete note:', error);
      throw new Error('Failed to delete note');
    }
  },

  /**
   * Get a specific note by ID
   */
  async getNote(noteId: string): Promise<Note> {
    try {
      const response = await api.get<DynamicToolResponse>(`${BASE_PATH}/${noteId}`);


      return transformToNote(response.data);
    } catch (error) {
      console.error('Failed to get note:', error);
      throw new Error('Failed to get note');
    }
  },

  /**
   * Get a note by name within a project
   */
  async getNoteByName(projectId: string, toolName: string): Promise<Note | null> {
    try {
      const response = await api.get<DynamicToolResponse>(`${BASE_PATH}/by-name/${encodeURIComponent(toolName)}`, {
        params: {
          project_id: projectId,
        },
      });


      return transformToNote(response.data);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null; // Note not found
      }
      console.error('Failed to get note by name:', error);
      throw new Error('Failed to get note by name');
    }
  },
};

export default notesApi;
